import { Component, computed, Input, OnChanges, OnInit, signal, SimpleChanges } from '@angular/core';
import { SelectOption } from '../../interfaces';
import { UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { isArray } from 'lodash';

const itemsPerVirtualPage = 200;

/**
 * Regular select when used with large collection of items is taking significant time to load.
 *
 * This select load just part of data and loads more if required.
 */
@Component({
  selector: 'vpfa-large-list-select',
  templateUrl: './large-list-select.component.html',
  styleUrls: ['./large-list-select.component.scss'],
})
export class LargeListSelectComponent implements OnInit, OnChanges {
  @Input() options: SelectOption[] = [];
  @Input() parentFormGroup: UntypedFormGroup;
  @Input() fc: UntypedFormControl;

  private currentPage = signal(1);
  private allOptions = signal<SelectOption[]>([]);
  private selectedOptions = signal<SelectOption[]>([]);
  private filter = signal<string>(null);

  constructor() {}

  ngOnChanges(changes: SimpleChanges): void {
    // handle new options
    if (!!changes['options']?.currentValue && changes['options'].currentValue != changes['options'].previousValue) {
      const newOptions = [...(changes['options'].currentValue ?? [])] as SelectOption[];

      newOptions.sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }));

      this.allOptions.set(newOptions);
    }

    // handle new form control - selected options
    if (!!changes['fc'].currentValue && changes['fc'].currentValue != changes['fc'].previousValue) {
      const value = changes['fc'].currentValue?.value;

      if (!value) {
        return;
      }

      if (isArray(value)) {
        this.selectedOptions.set(value);
      } else {
        this.selectedOptions.set([value]);
      }
    }
  }

  reducedOptions = computed(() => {
    const numberOfItemsToDisplay = this.currentPage() * itemsPerVirtualPage;

    const filter = this.filter();

    const items = [
      ...this.allOptions()
        .filter(x => {
          if (!filter) {
            return true;
          }

          return x.name?.toLowerCase().includes(filter?.toLowerCase());
        })
        .slice(0, numberOfItemsToDisplay),
    ];

    // add selected options to the list, so select component can render them correctly
    for (const selectedOption of this.selectedOptions()) {
      const found = items.find(x => String(x.value) === String(selectedOption.value));

      if (!found) {
        items.unshift(selectedOption);
      }
    }

    return items;
  });

  ngOnInit(): void {}

  onScrollToBottom() {
    const currentPage = this.currentPage();

    const noMoreItems = currentPage * itemsPerVirtualPage > this.allOptions()?.length;

    if (noMoreItems) {
      return;
    }

    this.currentPage.set(currentPage + 1);
  }

  onSearch(data: string) {
    this.filter.set(data === '' ? null : data);
  }
}
